import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PromptsService } from './promptsApi';
import { IPaginationPayload, IPaginationPayloadWithId } from '../../../../interfaces/shared/IPaginationPayload';
import { AxiosError } from 'axios';

const initialState: any = {
    promptsAreLoading: false,
    promptIsLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchPrompts = createAsyncThunk<any, IPaginationPayload>(
    'prompts/tryToFetchPrompts',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await PromptsService.tryToFetchPrompts(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result?.data;
    },
);

export const tryToFetchPromptsSources = createAsyncThunk<any, IPaginationPayloadWithId>(
    'prompts/tryToFetchPromptsSources',
    async ({ pageNumber, pageSize, id, filters, data }) => {
        const result = await PromptsService.tryToFetchPromptsSources(
            pageNumber,
            pageSize,
            id,
            data,
            filters,
        );
        return result;
    },
);

export const tryToFetchPromptsModal = createAsyncThunk<any, IPaginationPayload>(
    'prompts/tryToFetchPromptsModal',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await PromptsService.tryToFetchPrompts(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result?.data;
    },
);

export const tryToFetchSinglePrompt = createAsyncThunk<any, string>(
    'prompts/tryToFetchSinglePrompt',
    async (id: string) => {
        const result = await PromptsService.tryToFetchSinglePrompt(id);
        return result?.data;
    }
);

export const tryToFetchPromptIndustryRanking = createAsyncThunk<any, any>(
    'prompts/tryToFetchPromptIndustryRanking',
    async (data) => {
        const result = await PromptsService.tryToFetchPromptIndustryRanking(data?.id, data?.model);
        return result?.data;
    }
);

export const tryToFetchPromptIndustryRankingModal = createAsyncThunk<any, any>(
    'prompts/tryToFetchPromptIndustryRankingModal',
    async ({ pageNumber, pageSize, id }) => {
        const result = await PromptsService.tryToFetchPromptIndustryRanking(id, undefined, pageNumber, pageSize,);
        return result?.data;
    }
);

export const tryToFetchPromptModelDetails = createAsyncThunk<any, any>(
    'prompts/tryToFetchPromptModelDetails',
    async (data) => {
        const result = await PromptsService.tryToFetchPromptModelDetails(data?.id, data?.model);
        return result?.data;
    }
);

export const tryToGenerateNewPrompts = createAsyncThunk<any, void>(
    'models/tryToGenerateNewPrompts',
    async () => {
        const result = await PromptsService.tryToGenerateNewPrompts();
        return result?.data;
    }
);

export const tryToFetchPromptVoice = createAsyncThunk<any, string>(
    'models/tryToFetchPromptVoice',
    async (id) => {
        const result = await PromptsService.tryToFetchPromptVoice(id);
        return result?.data;
    }
);

export const tryToAcceptSuggestedPrompt = createAsyncThunk<any, any>(
    'prompts/tryToAcceptSuggestedPrompt',
    async (data, { rejectWithValue }) => {
        try {
            const result = await PromptsService.tryToAcceptSuggestedPrompt(data);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const tryToEditPrompt = createAsyncThunk<any, any>(
    'prompts/tryToEditPrompt',
    async (data, { rejectWithValue }) => {
        try {
            const result = await PromptsService.tryToEditPrompt(data);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const tryToAddPrompts = createAsyncThunk<any, any>(
    'prompts/tryToAddPrompts',
    async (data, { rejectWithValue }) => {
        try {
            const result = await PromptsService.tryToAddPrompts(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToDeletePrompt = createAsyncThunk<any, any>(
    'prompts/tryToDeletePrompt',
    async (data, { rejectWithValue }) => {
        try {
            const result = await PromptsService.tryToDeletePrompt(data);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const promptsSlice = createSlice({
    name: 'prompts',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchPrompts
            .addCase(tryToFetchPrompts.pending, (state) => {
                state.promptsAreLoading = true;
            })
            .addCase(tryToFetchPrompts.rejected, (state) => {
                state.promptsAreLoading = false;
            })
            .addCase(tryToFetchPrompts.fulfilled, (state, action) => {
                state.promptsAreLoading = false;
                state.prompts = action.payload;
            })

            // tryToFetchPromptIndustryRankingModal
            .addCase(tryToFetchPromptIndustryRankingModal.pending, (state) => {
                state.industryRankingAreLoading = true;
            })
            .addCase(tryToFetchPromptIndustryRankingModal.rejected, (state) => {
                state.industryRankingAreLoading = false;
            })
            .addCase(tryToFetchPromptIndustryRankingModal.fulfilled, (state, action) => {
                state.industryRankingAreLoading = false;
                state.industryRanking = action.payload;
            })

            // tryToFetchPromptsSources
            .addCase(tryToFetchPromptsSources.pending, (state) => {
                state.sourcesAreLoading = true;
            })
            .addCase(tryToFetchPromptsSources.rejected, (state) => {
                state.sourcesAreLoading = false;
            })
            .addCase(tryToFetchPromptsSources.fulfilled, (state, action) => {
                state.sourcesAreLoading = false;
                state.promptSources = action.payload;
            })

            // tryToFetchPromptsModal
            .addCase(tryToFetchPromptsModal.pending, (state) => {
                state.promptsModalAreLoading = true;
            })
            .addCase(tryToFetchPromptsModal.rejected, (state) => {
                state.promptsModalAreLoading = false;
            })
            .addCase(tryToFetchPromptsModal.fulfilled, (state, action) => {
                state.promptsModalAreLoading = false;
                state.promptsModal = action.payload;
            })

            // tryToFetchSinglePrompt
            .addCase(tryToFetchSinglePrompt.pending, (state) => {
                state.promptsIsLoading = true;
            })
            .addCase(tryToFetchSinglePrompt.rejected, (state) => {
                state.promptsIsLoading = false;
            })
            .addCase(tryToFetchSinglePrompt.fulfilled, (state, action) => {
                state.promptsIsLoading = false;
                state.promptDetails = action.payload?.data;
            })

            // tryToDeletePrompt
            .addCase(tryToDeletePrompt.pending, (state) => {
                state.promptIsLoading = true;
            })
            .addCase(tryToDeletePrompt.rejected, (state) => {
                state.promptIsLoading = false;
            })
            .addCase(tryToDeletePrompt.fulfilled, (state, action) => {
                state.promptIsLoading = false;
                if (state.promptsModal?.data.elements)
                    state.promptsModal.data.elements =
                        { ...state }.promptsModal?.data.elements.filter((prompt: any) => prompt?._id !== action?.meta?.arg?.id) || [];
                if (state.prompts?.data.elements)
                    state.prompts.data.elements =
                        { ...state }.prompts?.data.elements.filter((prompt: any) => prompt?._id !== action?.meta?.arg?.id) || [];
            })

            // tryToEditPrompt
            .addCase(tryToEditPrompt.pending, (state) => {
                state.promptIsLoading = true;
            })
            .addCase(tryToEditPrompt.rejected, (state) => {
                state.promptIsLoading = false;
            })
            .addCase(tryToEditPrompt.fulfilled, (state, action) => {
                state.promptIsLoading = false;
                if (state.prompts?.data.elements)
                    state.prompts.data.elements =
                        { ...state }.prompts?.data.elements.map((prompt: any) => {
                            if (prompt?._id === action?.meta?.arg?.id) {
                                return {
                                    ...prompt || {},
                                    prompt: action?.meta?.arg?.newPrompt,
                                    active: action?.meta?.arg?.active
                                }
                            } else {
                                return prompt
                            }
                        }) || [];
            })

            // tryToAddPrompts
            .addCase(tryToAddPrompts.pending, (state) => {
                state.promptIsLoading = true;
            })
            .addCase(tryToAddPrompts.rejected, (state) => {
                state.promptIsLoading = false;
            })
            .addCase(tryToAddPrompts.fulfilled, (state, action) => {
                state.promptIsLoading = false;
                if (state.promptsModal?.data.elements)
                    state.promptsModal.data.elements = [...state.promptsModal?.data.elements || [], ...action?.payload?.data || []]
                if (state.prompts?.data.elements)
                    state.prompts.data.elements = [...state.prompts?.data.elements || [], ...action?.payload?.data || []]
            })
    },
});

export default promptsSlice.reducer;